import { cartService } from '../services/api';
import { getPrimaryProductImageUrl } from './productImages';

export const GUEST_BAG_IMPORTED_EVENT = 'sheenwearGuestBagImported';

/**
 * Maps GET /cart line items to the guest localStorage / BagContext item shape.
 */
export function apiCartItemsToGuestItems(apiItems) {
  if (!Array.isArray(apiItems) || apiItems.length === 0) return [];

  const byKey = new Map();

  for (const line of apiItems) {
    const p = line.product;
    if (!p) continue;

    const productKey = p._id ?? p.id;
    if (productKey == null || productKey === '') continue;

    const selectedSize = line.size != null && line.size !== '' ? line.size : 'default';
    const selectedColor = line.color != null && line.color !== '' ? line.color : 'default';
    const itemId = `${productKey}_${selectedSize}_${selectedColor}`;

    const linePrice = line.price != null ? line.price : p.price;
    const qty = Math.max(1, Number(line.quantity) || 1);

    const guest = {
      itemId,
      id: p.id ?? p._id,
      _id: p._id,
      name: p.name,
      price: linePrice,
      originalPrice: line.originalPrice ?? p.originalPrice,
      image: getPrimaryProductImageUrl(p) || '',
      selectedSize,
      selectedColor,
      quantity: qty,
      category: p.category,
      subcategory: p.subcategory,
    };

    if (byKey.has(itemId)) {
      const prev = byKey.get(itemId);
      prev.quantity += qty;
    } else {
      byKey.set(itemId, guest);
    }
  }

  return [...byKey.values()];
}

function readGuestBagFromLocalStorage() {
  const raw = typeof localStorage !== 'undefined' ? localStorage.getItem('sheenwear_bag') : null;
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function persistGuestBagFromApiExport(items) {
  const list = Array.isArray(items) ? items : [];
  if (list.length === 0) {
    localStorage.removeItem('sheenwear_bag');
    localStorage.removeItem('sheenwear_bag_timestamp');
  } else {
    localStorage.setItem('sheenwear_bag', JSON.stringify(list));
    localStorage.setItem('sheenwear_bag_timestamp', Date.now().toString());
  }
  window.dispatchEvent(new CustomEvent(GUEST_BAG_IMPORTED_EVENT, { detail: list }));
}

/**
 * Call while auth token is still present: snapshot server cart into guest storage, then clear server cart
 * so the next login merge does not duplicate lines.
 */
export async function exportAuthenticatedCartToGuestBag() {
  if (!localStorage.getItem('authToken')) return;

  let cartRes = { success: false };
  let guestItems = [];
  try {
    cartRes = await cartService.getCart();
    let rawItems = [];
    if (cartRes?.data && Array.isArray(cartRes.data.items)) {
      rawItems = cartRes.data.items;
    } else if (Array.isArray(cartRes?.items)) {
      rawItems = cartRes.items;
    }
    guestItems = apiCartItemsToGuestItems(rawItems);
  } catch (e) {
    console.warn('exportAuthenticatedCartToGuestBag getCart:', e?.message || e);
    cartRes = { success: false };
  }

  const serverSnapshotOk = cartRes?.success === true;

  // Only clear server cart when we successfully read it; avoids a second failing request (and console
  // noise) when GET /cart already returned 5xx. Backend should still fix cart/coupon validation.
  if (serverSnapshotOk) {
    await cartService.clearCartQuiet();
    persistGuestBagFromApiExport(guestItems);
    return;
  }

  // Server cart unavailable — do not overwrite guest bag with an empty snapshot.
  const existingGuest = readGuestBagFromLocalStorage();
  if (existingGuest.length > 0) {
    persistGuestBagFromApiExport(existingGuest);
  } else if (guestItems.length > 0) {
    persistGuestBagFromApiExport(guestItems);
  }
}
